/**
 * 대화 안 이미지 현황 — 채팅 AI 요청에 `image_state` 로 동봉한다.
 *
 * 대화 안에서 만든 그림(P3 인라인 이미지)은 답변 스트림이 끝난 뒤 화면에서 따로 만들어져 모델 대화 기록에 없다.
 * 알려 주지 않으면 AI 가 "위에 만든 이미지는 없어요", "이 대화에서는 이미지를 만들 수 없어요"라고 답한다.
 *   { made, last_brief?, last_minutes_ago?, running?: { brief, elapsed_s } }
 * 모양은 백엔드 services/chat_image_state.normalize_image_state 와 같은 계약.
 */
import { maskCriticalPII } from './piiMask';

const BRIEF_MAX_CHARS = 160;
const ORIGINAL_REQUEST_MARKER = '\n\n[선생님 원문 요청';

const toMillis = (value) => {
  if (!value) return null;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  const parsed = typeof value === 'number' ? value : Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const shortBrief = (text) => {
  const raw = String(text || '');
  const cut = raw.indexOf(ORIGINAL_REQUEST_MARKER);
  const brief = (cut >= 0 ? raw.slice(0, cut) : raw).replace(/\s+/g, ' ').trim();
  if (!brief) return '';
  return maskCriticalPII(brief).text.slice(0, BRIEF_MAX_CHARS);
};

const isMadeImage = (message) => Boolean(
  message
  && message.role === 'assistant'
  && message.hasImage
  && message.imageUrl
);

export const buildChatImageState = ({ messages = [], job = null, conversationId = null, now = Date.now() } = {}) => {
  const images = (Array.isArray(messages) ? messages : []).filter(isMadeImage);
  const runningJob = job && job.conversationId && job.conversationId === conversationId ? job : null;
  if (!images.length && !runningJob) return null;
  const state = { made: images.length };
  const last = images[images.length - 1];
  if (last) {
    const brief = shortBrief(last.inline_image?.source_prompt || last.content);
    if (brief) state.last_brief = brief;
    const madeAt = toMillis(last.timestamp);
    if (madeAt != null) state.last_minutes_ago = Math.max(0, Math.round((now - madeAt) / 60000));
  }
  if (runningJob) {
    const startedAt = toMillis(runningJob.startedAt);
    state.running = {
      brief: shortBrief(runningJob.prompt),
      elapsed_s: startedAt != null ? Math.max(0, Math.round((now - startedAt) / 1000)) : 0,
    };
  }
  return state;
};

const pending = new Map();

/** 보낼 요청의 화면 대화 ID 로 현황을 맡겨 둔다. chatApi 가 같은 ID 로 한 번 꺼내 쓴다. */
export const stashChatImageState = (conversationId, state) => {
  if (!conversationId) return;
  if (state) pending.set(conversationId, state);
  else pending.delete(conversationId);
};

export const takeChatImageState = (conversationId) => {
  if (!conversationId || !pending.has(conversationId)) return null;
  const state = pending.get(conversationId);
  pending.delete(conversationId);
  return state;
};
